import type { CareerSave, CareerStatistics, PlayerProfile } from './types';
import { cloneCanonicalWorldHistory } from './WorldHistory';
import { TOUR_GOLFERS } from './TourGolfers';
import { INAUGURAL_SEASON } from './SeasonDefinition';
import { captureLeaderboardSnapshot, TournamentForecastEngine } from './TournamentBroadcast';

export const CAREER_SCHEMA_VERSION = 4;
export const ACTIVE_TOURNAMENT_SCHEMA_VERSION = 1;
export const CAREER_STORAGE_KEY = 'tournament-golf.careers';
export const CAREER_BACKUP_STORAGE_KEY = `${CAREER_STORAGE_KEY}.backup`;

export class CareerSaveError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'CareerSaveError';
  }
}
export interface CareerRepository {
  createCareer(profile: Omit<PlayerProfile, 'id' | 'careerRanking'>, slot: number): CareerSave;
  saveCareer(save: CareerSave): void;
  loadCareer(slot: number): CareerSave | null;
  listCareers(): CareerSave[];
  deleteCareer(slot: number): void;
  exportCareer(slot: number): string;
  importCareer(serialized: string, slot?: number): CareerSave;
  migrateCareer(value: unknown): CareerSave;
}
const stats = (): CareerStatistics => ({
  seasonsPlayed: 1,
  eventsPlayed: 0,
  roundsPlayed: 0,
  tournamentsCompleted: 0,
  cutsMade: 0,
  cutsMissed: 0,
  wins: 0,
  runnerUpFinishes: 0,
  top5s: 0,
  top10s: 0,
  majorStarts: 0,
  majorCutsMade: 0,
  majorTop10s: 0,
  bestMajorFinish: null,
  majorWins: 0,
  seasonChampionshipQualifications: 0,
  seasonChampionshipStarts: 0,
  seasonChampionshipWins: 0,
  bestSeasonChampionshipFinish: null,
  seasonTitles: 0,
  seasonChampionships: 0,
  scoringAverage: 0,
  bestRound: null,
  worstRound: null,
  birdies: 0,
  eagles: 0,
  pars: 0,
  bogeys: 0,
  doublesOrWorse: 0,
  longestDrive: 0,
  longestPutt: 0,
  headToHead: {},
});
const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

export class LocalCareerRepository implements CareerRepository {
  lastRecovery: string | null = null;
  constructor(private readonly storage: Storage = window.localStorage) {}

  private decode(raw: string | null): CareerSave[] {
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new CareerSaveError('Career payload must be an array');
    return parsed.map((value) => this.migrateCareer(value));
  }
  private all(): CareerSave[] {
    this.lastRecovery = null;
    try {
      return this.decode(this.storage.getItem(CAREER_STORAGE_KEY));
    } catch (primaryError) {
      try {
        const recovered = this.decode(this.storage.getItem(CAREER_BACKUP_STORAGE_KEY));
        this.lastRecovery = 'Recovered careers from the last valid backup.';
        return recovered;
      } catch (backupError) {
        throw new CareerSaveError('Primary and backup career saves are invalid', {
          primaryError,
          backupError,
        });
      }
    }
  }
  private write(value: CareerSave[]) {
    value.forEach((save) => this.validate(save));
    const next = JSON.stringify(value);
    if (this.storage.getItem(CAREER_STORAGE_KEY) === next) return;
    const prior = this.storage.getItem(CAREER_STORAGE_KEY);
    if (prior !== null) {
      try {
        this.decode(prior);
        this.storage.setItem(CAREER_BACKUP_STORAGE_KEY, prior);
      } catch {
        /* Never replace a valid backup with malformed primary data. */
      }
    }
    try {
      this.storage.setItem(CAREER_STORAGE_KEY, next);
      this.decode(this.storage.getItem(CAREER_STORAGE_KEY));
    } catch (error) {
      throw new CareerSaveError('Career save could not be verified after writing', error);
    }
  }
  private validate(save: CareerSave) {
    if (!isRecord(save) || save.schemaVersion !== CAREER_SCHEMA_VERSION)
      throw new CareerSaveError('Unsupported career schema');
    if (!Number.isInteger(save.slot) || save.slot < 1 || save.slot > 3)
      throw new CareerSaveError('Career slot must be 1, 2, or 3');
    if (!save.careerId || !isRecord(save.playerProfile) || !save.playerProfile.id)
      throw new CareerSaveError('Career identity is missing');
    if (!Number.isInteger(save.revision) || save.revision < 0)
      throw new CareerSaveError('Career revision is invalid');
    if (save.activeTournamentState) {
      const active = save.activeTournamentState;
      if (
        active.schemaVersion !== ACTIVE_TOURNAMENT_SCHEMA_VERSION ||
        active.courseId !== active.tournamentState.courseId ||
        active.eventId !== active.tournamentState.eventId ||
        active.playerRound.activeHoleNumber < 1 ||
        active.playerRound.activeHoleNumber > 18
      )
        throw new CareerSaveError('Active tournament checkpoint is invalid');
    }
  }
  createCareer(profile: Omit<PlayerProfile, 'id' | 'careerRanking'>, slot: number) {
    if (slot < 1 || slot > 3) throw new CareerSaveError('Career slot must be 1, 2, or 3');
    if (this.all().some((career) => career.slot === slot))
      throw new CareerSaveError('Career slot is occupied');
    const now = new Date().toISOString();
    const careerId = globalThis.crypto?.randomUUID?.() ?? `career-${Date.now()}`;
    const save: CareerSave = {
      schemaVersion: CAREER_SCHEMA_VERSION,
      revision: 0,
      careerId,
      slot,
      playerProfile: { ...profile, id: `player-${careerId}`, careerRanking: 49 },
      currentSeasonNumber: 1,
      currentEventIndex: 0,
      seasonStatus: 'inProgress',
      seasonState: { points: 0, standingsRank: 49, form: 0, results: [] },
      activeTournamentState: null,
      careerStatistics: stats(),
      courseStatistics: {},
      eventHistory: [],
      seasonHistory: [],
      qualifications: [],
      rankingHistory: [49],
      rivalryState: { rivalId: 'alex-carter', headline: 'A new rivalry begins with Alex Carter' },
      progressionState: { skillPoints: 0 },
      narrativeFlags: ['firstProfessionalEvent'],
      worldHistory: cloneCanonicalWorldHistory(),
      golferForm: Object.fromEntries(
        TOUR_GOLFERS.map((golfer, index) => [
          golfer.id,
          index % 11 === 0
            ? 'excellent'
            : index % 7 === 0
              ? 'poor'
              : index % 3 === 0
                ? 'good'
                : 'neutral',
        ]),
      ),
      playerCareerStakes: [],
      broadcastSettings: { frequency: 'balanced', autoOpenPostHole: true },
      createdAt: now,
      updatedAt: now,
    };
    this.saveCareer(save);
    return save;
  }
  saveCareer(save: CareerSave) {
    const all = this.all();
    const stored = all.find((career) => career.slot === save.slot);
    const next = {
      ...save,
      schemaVersion: CAREER_SCHEMA_VERSION,
      revision: Math.max(save.revision ?? 0, stored?.revision ?? 0) + 1,
      updatedAt: new Date().toISOString(),
    };
    Object.assign(save, next);
    this.write([...all.filter((career) => career.slot !== save.slot), next]);
  }
  loadCareer(slot: number) {
    return this.all().find((career) => career.slot === slot) ?? null;
  }
  listCareers() {
    return this.all().sort((a, b) => a.slot - b.slot);
  }
  deleteCareer(slot: number) {
    this.write(this.all().filter((career) => career.slot !== slot));
  }
  exportCareer(slot: number) {
    const career = this.loadCareer(slot);
    if (!career) throw new CareerSaveError('Career slot is empty');
    return JSON.stringify(career, null, 2);
  }
  importCareer(serialized: string, slot?: number) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(serialized);
    } catch (error) {
      throw new CareerSaveError('Import is not valid JSON', error);
    }
    const career = this.migrateCareer(parsed);
    if (slot !== undefined) career.slot = slot;
    this.saveCareer(career);
    return career;
  }
  migrateCareer(value: unknown): CareerSave {
    if (!isRecord(value)) throw new CareerSaveError('Invalid career save');
    const source = value as unknown as CareerSave;
    if (!source.careerId || !source.playerProfile || !source.seasonState)
      throw new CareerSaveError('Career save is missing required data');
    // Version 2 stored TournamentState directly; it cannot safely restore balls, so retain
    // the career and discard only that incomplete active round.
    const active =
      source.schemaVersion >= 3 && isRecord(source.activeTournamentState)
        ? source.activeTournamentState
        : null;
    const worldHistory = source.worldHistory ?? cloneCanonicalWorldHistory();
    const golferForm =
      source.golferForm ?? Object.fromEntries(TOUR_GOLFERS.map((golfer) => [golfer.id, 'neutral']));
    if (active) {
      const event = INAUGURAL_SEASON.events.find((candidate) => candidate.id === active!.eventId);
      if (event) {
        active.priorHoleLeaderboardSnapshot ??= captureLeaderboardSnapshot(
          active.tournamentState,
          active.playerRound.activeHoleNumber,
        );
        active.narrativeMemory ??= {
          recentlyShownKeys: [],
          golferMentionCounts: {},
          categoryCooldowns: {},
        };
        active.simulatedHoleEvents ??= [];
        active.forecast ??= new TournamentForecastEngine().generate(
          event,
          TOUR_GOLFERS,
          worldHistory,
          golferForm,
          source.currentSeasonNumber,
        );
      }
    }
    const migrated: CareerSave = {
      ...source,
      schemaVersion: CAREER_SCHEMA_VERSION,
      revision: source.revision ?? 0,
      seasonStatus: source.seasonStatus ?? 'inProgress',
      activeTournamentState: active,
      careerStatistics: { ...stats(), ...(source.careerStatistics ?? {}) },
      courseStatistics: source.courseStatistics ?? {},
      qualifications: source.qualifications ?? [],
      eventHistory: source.eventHistory ?? [],
      seasonHistory: source.seasonHistory ?? [],
      rankingHistory: source.rankingHistory ?? [source.playerProfile.careerRanking],
      narrativeFlags: source.narrativeFlags ?? [],
      progressionState: source.progressionState ?? {},
      worldHistory,
      golferForm,
      playerCareerStakes: source.playerCareerStakes ?? [],
      broadcastSettings: source.broadcastSettings ?? {
        frequency: 'balanced',
        autoOpenPostHole: true,
      },
    };
    this.validate(migrated);
    return migrated;
  }
}
